import { BlogBodyEditor } from "@/components/studio/blog-body-editor";
import { MediaPreview } from "@/components/studio/file-upload";
import { UploadDropzone } from "@/components/studio/upload-dropzone";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  THUMBNAIL_ACCEPT_ATTR,
  THUMBNAIL_HINT,
} from "@/lib/thumbnail-validation";

interface VariantData {
  languageCode: string;
  isDefault: boolean;
  localizedTitle: string;
  localizedDescription: string;
  localizedBody: string | null;
  thumbnailUrl: string | null;
}

interface BlogVariantFieldsProps {
  variant: VariantData;
  onFieldChange: (field: keyof VariantData, value: any) => void;
  onFileUpload: (
    e: React.ChangeEvent<HTMLInputElement>,
    field: "thumbnailUrl",
  ) => void;
  isUploading: boolean;
  isReadOnly?: boolean;
}

export function BlogVariantFields({
  variant,
  onFieldChange,
  onFileUpload,
  isUploading,
  isReadOnly = false,
}: BlogVariantFieldsProps) {
  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label>Title</Label>
        <Input
          value={variant.localizedTitle}
          onChange={(e) => onFieldChange("localizedTitle", e.target.value)}
          placeholder="Enter blog title"
          disabled={isReadOnly}
          data-testid="input-variant-title"
        />
      </div>

      <div className="space-y-2">
        <Label>Description</Label>
        <Textarea
          value={variant.localizedDescription}
          onChange={(e) =>
            onFieldChange("localizedDescription", e.target.value)
          }
          placeholder="Enter blog description / intro"
          rows={3}
          disabled={isReadOnly}
          data-testid="input-variant-description"
        />
      </div>

      <div className="space-y-2">
        <Label>Body</Label>
        <BlogBodyEditor
          value={variant.localizedBody}
          onChange={(val) => onFieldChange("localizedBody", val)}
          disabled={isReadOnly}
        />
      </div>

      <div className="space-y-2">
        <Label>Thumbnail</Label>
        {variant.thumbnailUrl ? (
          <MediaPreview
            url={variant.thumbnailUrl}
            mediaType="image"
            onRemove={
              isReadOnly ? undefined : () => onFieldChange("thumbnailUrl", null)
            }
            onReplace={
              isReadOnly ? undefined : (e) => onFileUpload(e, "thumbnailUrl")
            }
            replaceAccept={THUMBNAIL_ACCEPT_ATTR}
            isReadOnly={isReadOnly}
          />
        ) : (
          <UploadDropzone
            type="image"
            label="Upload Thumbnail"
            hint={THUMBNAIL_HINT}
            accept={THUMBNAIL_ACCEPT_ATTR}
            onChange={(e) => onFileUpload(e, "thumbnailUrl")}
            isUploading={isUploading}
            disabled={isReadOnly}
            testId="button-upload-thumbnail"
          />
        )}
      </div>
    </div>
  );
}
